import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import {
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorState, PageHeader, SectionCard, StateDisplay } from '@/components/common';
import { DetailPageSkeleton } from '@/components/common/Skeletons';
import { useToast } from '@/hooks/useToast';
import { formatShortDate } from '@/utils/dateUtils';

import { useNotificationHistory } from '../hooks/useNotificationHistory';
import type { Notification, NotificationType } from '../types/notifications.types';

/** A representative icon per notification category (mirrors the drawer's mapping). */
function iconFor(type: NotificationType): ReactNode {
  switch (type) {
    case 'INTERVIEW_SCHEDULED':
    case 'INTERVIEW_REMINDER':
      return <EventOutlinedIcon fontSize="small" />;
    case 'APPLICATION_STATUS_CHANGED':
    case 'APPLICATION_STALLED':
    case 'JOB_POSTED':
      return <WorkOutlineIcon fontSize="small" />;
    default:
      return <NotificationsNoneOutlinedIcon fontSize="small" />;
  }
}

/** Compact "time ago" for a notification's createdAt — falls back to a date past a week. */
function timeAgo(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatShortDate(iso);
}

/**
 * Notification history — the "View all" page. The bell drawer shows only the newest
 * page; this page pages through the entire list with "Load more", reusing the backend's offset
 * pagination. Clicking an unread row marks it read (in place) and follows its deep link, exactly like
 * the drawer. Reachable from the drawer footer and by every persona (it lives under /account). The
 * drawer itself is unchanged.
 */
export function NotificationHistoryPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const {
    notifications,
    totalCount,
    hasNextPage,
    loadMore,
    loadingMore,
    markRead,
    loading,
    error,
    refetch,
  } = useNotificationHistory();

  const openNotification = async (notification: Notification) => {
    if (!notification.isRead) {
      const errors = await markRead(notification.id);
      if (errors.length > 0) {
        toast.showError(errors[0].message);
        return;
      }
    }
    if (notification.deepLink) navigate(notification.deepLink);
  };

  if (loading) return <DetailPageSkeleton />;
  if (error) {
    return <ErrorState title="Could not load your notifications" onRetry={() => void refetch()} />;
  }

  return (
    <Stack spacing={3}>
      <PageHeader
        title="All notifications"
        description="Everything WillnQ has sent you, newest first."
      />

      {notifications.length === 0 ? (
        <StateDisplay
          tone="secondary"
          icon={<NotificationsNoneOutlinedIcon />}
          title="You're all caught up"
          description="You have no notifications yet. We'll let you know when something needs your attention."
        />
      ) : (
        <SectionCard title={`${totalCount} notification${totalCount === 1 ? '' : 's'}`}>
          <List sx={{ py: 0 }}>
            {notifications.map((notification, index) => (
              <Box key={notification.id}>
                {index > 0 ? <Divider component="li" /> : null}
                <ListItemButton
                  onClick={() => openNotification(notification)}
                  aria-label={`${notification.title}${notification.isRead ? '' : ' (unread)'}`}
                  sx={{ alignItems: 'flex-start', py: 1.5, px: 2, gap: 0.5 }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      mt: 0.25,
                      color: notification.isRead ? 'text.secondary' : 'primary.main',
                    }}
                  >
                    {iconFor(notification.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={notification.title}
                    secondary={
                      <>
                        <Typography variant="caption" color="text.secondary" component="span">
                          {notification.body}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          component="span"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {timeAgo(notification.createdAt)}
                        </Typography>
                      </>
                    }
                    slotProps={{
                      primary: {
                        variant: 'body2',
                        fontWeight: notification.isRead ? 400 : 700,
                      },
                    }}
                  />
                  {/* Unread indicator — a primary dot paired with the bold title (never color-only). */}
                  {!notification.isRead ? (
                    <Box
                      aria-hidden
                      sx={{
                        mt: 1,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        flexShrink: 0,
                      }}
                    />
                  ) : null}
                </ListItemButton>
              </Box>
            ))}
          </List>

          {hasNextPage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
              <Button onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </Box>
          ) : null}
        </SectionCard>
      )}
    </Stack>
  );
}
